import React, { useEffect, useReducer, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import client from "@culturehq/client";
import { Icon, classnames, PlainButton } from "@culturehq/components";
import { displayLastName, t } from "app/utils";
import getUserName from "app/utils/getUserName";
import styles from "./user-slider.module.scss";

const initialState = {
  appending: null,
  error: null,
  fetching: true,
  pagination: {},
  users: [],
  seed: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case "appendStart":
      return { ...state, appending: state.pagination.currentPage + 1 };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        users: [...state.users, ...action.response.users],
        seed: action.response.seed
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        pagination: action.response.pagination,
        users: action.response.users,
        seed: action.response.seed
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    default:
      return state;
  }
};

const useFetchUsers = (dispatch, queryParams, organization, preview, seed) => useEffect(
  () => {
    if (!preview) {
      let cancelled = false;
      dispatch({ type: "fetchStart" });

      client.listOrganizationLandingPagesUsers({
        ...queryParams,
        seed,
        organization
      })
        .then(response => {
          if (!cancelled) {
            dispatch({ type: "fetchSuccess", response });
          }
        })
        .catch(error => {
          if (!cancelled) {
            dispatch({ type: "fetchError", error });
          }
        });

      return () => {
        cancelled = true;
      };
    }

    return undefined;
  },
  [dispatch, queryParams, organization, preview, seed]
);

const useAppendUsers = (dispatch, appending, queryParams, organization, preview, seed) => useEffect(
  () => {
    if (appending && !preview) {
      let cancelled = false;

      client.listOrganizationLandingPagesUsers({
        ...queryParams,
        seed,
        page: appending,
        organization
      })
        .then(response => {
          if (!cancelled) {
            dispatch({ type: "appendSuccess", response });
          }
        })
        .catch(error => {
          if (!cancelled) {
            dispatch({ type: "appendError", error });
          }
        });

      return () => {
        cancelled = true;
      };
    }

    return undefined;
  },
  [dispatch, appending, queryParams, organization, preview, seed]
);

const getSlideLayout = (
  index,
  containerRef,
  sliderRef,
  users
) => {
  const USER_WIDTH = 200;
  let percent = index * (100 / (users.length + 1));
  const left = index !== 0;
  let right = index !== users.length && users.length > 4;

  const container = containerRef.current;
  const slider = sliderRef.current;
  const sliderWidth = USER_WIDTH * (users.length + 1);

  if (!(container instanceof HTMLDivElement) || !(slider instanceof HTMLDivElement)) {
    return { percent, left, right };
  }

  const pixels = (percent * sliderWidth) / 100;
  const correction = pixels - (slider.clientWidth - container.clientWidth);

  if (sliderWidth <= container.clientWidth) {
    right = false;
  }

  if (correction > 0) {
    percent -= (correction / sliderWidth) * 100;
    right = false;
  }

  return { percent, left, right };
};

const UsersSlider = ({
  preview = false, queryParams, organization, children, users: usersState, isEducational
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useFetchUsers(dispatch, queryParams, organization, preview, state.seed);
  useAppendUsers(dispatch, state.appending, queryParams, organization, preview, state.seed);

  const history = useHistory();
  const [index, setIndex] = useState(0);
  const onNext = () => setIndex(value => value + 1);
  const onPrev = () => setIndex(value => value - 1);

  const finalUsers = usersState || state.users;
  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const slideLayout = getSlideLayout(index, containerRef, sliderRef, finalUsers);

  useEffect(
    () => {
      if (!slideLayout.right && finalUsers.length !== 0
        && state.pagination.currentPage !== state.pagination.totalPages) {
        dispatch({ type: "appendStart" });
      }
    },
    [slideLayout.right, state.pagination, finalUsers, state.appending]
  );

  const handleUserClick = user => {
    if (preview) {
      history.push(`/admin/landing-page/preview/users/${user.id}`);
      return;
    }

    history.push(`/users/${user.id}`);
  };

  return (
    <div className="Preview-Browser__People">
      {finalUsers.length > 0 && (
        <>
          {children}
          <div style={{ position: "relative" }}>
            <button
              aria-label={t("Previous")}
              type="button"
              className={classnames(styles.direction, styles.left)}
              onClick={onPrev}
              disabled={!slideLayout.left}
            >
              <Icon icon="chevron-left" />
            </button>
            <button
              aria-label={t("Next")}
              type="button"
              className={classnames(styles.direction, styles.right)}
              onClick={onNext}
              disabled={!slideLayout.right}
            >
              <Icon icon="chevron-right" />
            </button>
            <div ref={containerRef} className={`${finalUsers.length === 0 ? styles.emptyContainer : styles.container} no-scrollbar`}>
              <div
                ref={sliderRef}
                className={`${styles.slider} ${finalUsers.length === 0 ? styles.sliderEmpty : ""} Users-Slider ${finalUsers.length === 0 ? "Users-Slider__Empty" : ""}`}
                style={{ transform: `translateX(-${slideLayout.percent}%)` }}
              >
                {finalUsers.map(user => (
                  <PlainButton
                    aria-labelledby={user.id}
                    className={`${styles.card}`}
                    key={user.id}
                    style={{ backgroundImage: `url(${user.avatar.mediumUrl})` }}
                    onClick={() => handleUserClick(user)}
                  >
                    <div id={user.id} style={{ display: "none" }}>{getUserName(user, displayLastName(organization))}</div>
                    <div className="Users-Slider__overlay">
                      <p className={styles.label}>
                        {getUserName(user, displayLastName(organization))}
                      </p>
                      {isEducational && user.programs.length > 0 ? (
                        <p className={styles.sublabel}>{user.programs[0].name}</p>
                      ) : (
                        <p className={styles.sublabel}>{user.title}</p>
                      )}
                    </div>
                  </PlainButton>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UsersSlider;
